import { useEffect, useState } from 'react';
import { api } from './api';
import auth from './auth';
import { canAppPurchase } from './iap';

declare global {
  interface Window {
    TossPayments?: any;
  }
}

export interface BillingInfo {
  enabled: boolean;
  client_key: string;
  customer_key: string;
  amount: number;
  order_name: string;
  migration: boolean; // 페이소프트 회원: 카드 등록만 하고 first_charge_at 부터 결제
  first_charge_at: string | null;
  subscription: {
    status: 'active' | 'past_due' | 'canceled' | 'ended';
    card_company: string | null;
    card_number: string | null;
    current_period_end: string | null;
    next_billing_at: string | null;
  } | null;
}

// 앱(웹뷰) 안에서는 결제 화면을 보여주지 않는다 (스토어 결제 정책). 구독은 웹에서만 받는다.
export const isApp = () => /chordscore\/(Android|iOS)/.test(navigator.userAgent);
// 앱(웹뷰) 안에서는 구독·가격 안내를 아예 보여 주지 않는다 — 애플 3.1.1·구글 플레이 결제 정책 (앱 안 디지털 구독은 스토어 결제만).
// 결제는 웹(chordscore.com)에서만, 앱은 로그인해서 보기만 (2026-10-09 사장님 결정: 아이폰, 같은 날 안드로이드도)
// 2026-10-10: 안드로이드 앱 2.1.0+ 는 구글 플레이 결제로 앱 안에서 구독 (canAppPurchase) → 구독 안내를 보여 주고 /pricing 은 앱 구독 화면
export const noPurchaseUI = () => /chordscore\/(iOS|Android)/.test(navigator.userAgent) && !canAppPurchase();

// 카드 자동결제를 켤 때 빌드 환경변수 REACT_APP_CARD_BILLING=true 로 빌드한다.
// 꺼져 있을 때 /billing 을 부르면 서버에 없는 주소라 공통 오류 처리가 'Error 404' 경고창을 띄운다.
const CARD_BILLING_ENABLED = process.env.REACT_APP_CARD_BILLING === 'true';

export const getBillingInfo = async (): Promise<BillingInfo | null> => {
  if (!CARD_BILLING_ENABLED || !auth.getToken()) return null;
  try {
    const { data } = await api.get('/billing');
    return data.success ? data.data : null;
  } catch (e) {
    return null;
  }
};

/** 카드 자동결제가 켜져 있으면 BillingInfo, 아니면(예전 수동 구독 방식) null */
export const useBilling = () => {
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  useEffect(() => {
    getBillingInfo().then((info) => setBilling(info && info.enabled ? info : null));
  }, []);
  return billing;
};

const loadTossSdk = () =>
  new Promise<void>((resolve, reject) => {
    if (window.TossPayments) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.tosspayments.com/v2/standard';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('토스페이먼츠 SDK를 불러오지 못했습니다.'));
    document.head.appendChild(script);
  });

/** 토스 카드 등록창을 띄운다. 등록이 끝나면 /billing/success 로 돌아온다. */
export const startCardSubscription = async (billing: BillingInfo) => {
  try {
    await loadTossSdk();
    const payment = window.TossPayments(billing.client_key).payment({ customerKey: billing.customer_key });
    await payment.requestBillingAuth({
      method: 'CARD',
      successUrl: `${window.location.origin}/billing/success`,
      failUrl: `${window.location.origin}/billing/fail`,
    });
  } catch (e) {
    // 사용자가 카드 등록창을 닫은 경우는 조용히 넘어간다
    if (e && e.code === 'USER_CANCEL') return;
    alert((e && e.message) || '카드 등록창을 열지 못했습니다. 잠시 후 다시 시도해주세요.');
  }
};
